// @givmo/board — the register, grouped by person, with the annual answers in it (CBG-YR2-WEB1; frame
// o8-register).
//
// ══ STILL A READ, AND STILL NEVER A CONCLUSION (v0's rule, `conflicts-page.tsx`) ════════════════
//
// The rows are the backend's register read; this view gathers each person's rows under their name
// (the contract leaves grouping to the web, §10.1) and orders them by date (`groupByPerson`). Each
// person's standing annual disclosure shows the names its answers give, with "New since 2026" where an
// interest is not in the answers it replaced — a mechanical comparison the backend made of two
// records, said in plain words, never a pill and never a colour. Single disclosures sit beside it. No
// count of conflicts, no seriousness, no verdict, anywhere; a person with nothing recorded reads as
// exactly that.
//
// ══ EACH ENTRY CITES ITS RECORD (CBG-YR2-WEB3; frame y4) ════════════════════════════════════════
//
// The register is evidence, so each entry says where it comes from, quietly, as v0's flat list did:
// its record in the minute book; the meeting a single disclosure was made at; and, for annual
// answers corrected since, the correction whose words the entry shows (contract §10.1), with
// "corrected" beside the entry's date. Each is a link to the record itself, never a claim about it,
// and a pointer this reader may not follow arrives absent from the backend (v0's rule).

import type { BoardCoiCycleOut, OrgBoardMemberOut } from '@givmo/api-client';
import { Card, CardBody } from '@givmo/design-system';
import { BOARD_PAGES } from '../../board-routes';
import { Link } from '../../board-routing';
import { displayDate } from '../../lib/display';
import { capacityLine } from '../roster/officer-roles';
import type { ConflictDisclosureRow } from './conflicts-decode';
import { interestDescription } from './coi-decode';
import { PersonCell } from './coi-parts';
import { coiWords as w } from './coi-words';

const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';
/** A citation: small, grey and underlined, so it reads as a source and the answers stay the subject;
 *  the brand colour comes on hover and focus (y4). */
const CITE =
  'font-sans text-s12 font-w500 tracking-givmo text-text-tertiary underline decoration-border-default underline-offset-2 outline-none transition-colors hover:text-brand-700 hover:decoration-brand-700 focus-visible:text-brand-700 focus-visible:decoration-brand-700';

interface PersonGroup {
  seatId: string;
  name: string;
  entries: ConflictDisclosureRow[];
}

/** When an entry was made, as the record says it: its date, then when it was filed. */
function when(entry: ConflictDisclosureRow): string {
  return `${entry.disclosed_on ?? ''} ${entry.recorded_at ?? ''}`;
}

/**
 * The register's rows under each person (§10.1 leaves grouping to the web). The backend serves the
 * register oldest first; a person's card reads newest first — their annual answers, then what they
 * disclosed on its own — and the people are ordered by their most recent disclosure, newest first,
 * with anyone the open year asks who has nothing recorded after them. Dates only: nothing is ranked.
 */
export function groupByPerson(
  entries: readonly ConflictDisclosureRow[],
  names: Map<string, string>,
  alsoAsked: readonly { board_member_id: string; display_name?: string | null }[],
): PersonGroup[] {
  const groups = new Map<string, PersonGroup>();
  for (const entry of entries) {
    const seatId = entry.disclosing_board_member_id;
    const group = groups.get(seatId) ?? {
      seatId,
      name: entry.disclosing_member_name ?? names.get(seatId) ?? '',
      entries: [],
    };
    group.entries.push(entry);
    groups.set(seatId, group);
  }
  const newestFirst = (a: ConflictDisclosureRow, b: ConflictDisclosureRow) =>
    when(b).localeCompare(when(a));
  const recorded = [...groups.values()].map((group) => ({
    ...group,
    entries: [
      ...group.entries.filter((entry) => entry.annual !== null).sort(newestFirst),
      ...group.entries.filter((entry) => entry.annual === null).sort(newestFirst),
    ],
  }));
  const latest = (group: PersonGroup) =>
    group.entries.reduce((max, entry) => (when(entry) > max ? when(entry) : max), '');
  recorded.sort((a, b) => latest(b).localeCompare(latest(a)));
  // Someone the open year asks who has nothing recorded yet still has a place (o8-register's
  // "2027 answers: not submitted yet").
  const unrecorded = alsoAsked
    .filter((person) => !groups.has(person.board_member_id))
    .map((person) => ({
      seatId: person.board_member_id,
      name: person.display_name ?? names.get(person.board_member_id) ?? '',
      entries: [],
    }));
  return [...recorded, ...unrecorded];
}

export function GroupedRegister({
  entries,
  seats,
  cycle,
  filtered,
}: {
  entries: readonly ConflictDisclosureRow[];
  seats: readonly OrgBoardMemberOut[];
  /** The open year, when there is one: who it asks, and who hasn't answered. */
  cycle: BoardCoiCycleOut | null;
  /** Whether a filter narrows the list (then nobody is added from the year). */
  filtered: boolean;
}) {
  const names = new Map(seats.map((seat) => [seat.id, seat.display_name]));
  const seatsById = new Map(seats.map((seat) => [seat.id, seat]));
  const waiting = new Map(
    (cycle?.people ?? [])
      .filter((person) => person.status === 'not_submitted' || person.status === 'not_sent')
      .map((person) => [person.board_member_id, person]),
  );
  const groups = groupByPerson(
    entries,
    names,
    filtered ? [] : (cycle?.people ?? []).filter((p) => p.status !== 'left'),
  );

  return (
    <ul className="flex flex-col gap-12" data-testid="coi-register-list">
      {groups.map((group) => {
        const seat = seatsById.get(group.seatId);
        const annualEntries = group.entries.filter((entry) => entry.annual);
        const latest =
          annualEntries.find((entry) => entry.status === 'active') ?? annualEntries[0] ?? null;
        return (
          <li key={group.seatId} data-testid={`coi-register-${group.seatId}`}>
            <Card>
              <CardBody className="flex flex-col gap-12">
                <div className="flex flex-wrap items-start justify-between gap-8">
                  <PersonCell name={group.name} capacity={seat ? capacityLine(seat) : ''} />
                  {latest?.annual ? (
                    <Link
                      to={BOARD_PAGES.coiResponse}
                      params={{ cycleId: latest.annual.cycle_id, recordId: latest.record_id }}
                      className={LINK}
                      data-testid={`coi-register-answers-${group.seatId}`}
                    >
                      {w.answersOf(latest.annual.cycle_label)}
                    </Link>
                  ) : null}
                </div>
                {group.entries.map((entry) =>
                  entry.annual ? (
                    <AnnualBlock key={entry.record_id} entry={entry} />
                  ) : (
                    <SingleBlock key={entry.record_id} entry={entry} />
                  ),
                )}
                {cycle !== null && waiting.has(group.seatId) ? (
                  <span
                    className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                    data-testid={`coi-register-not-yet-${group.seatId}`}
                  >
                    {w.notSubmittedYet(cycle.label)}
                  </span>
                ) : null}
              </CardBody>
            </Card>
          </li>
        );
      })}
    </ul>
  );
}

/** One annual disclosure: its year and how it was given, the names it gives, what it replaced. */
function AnnualBlock({ entry }: { entry: ConflictDisclosureRow }) {
  const annual = entry.annual;
  if (!annual) return null;
  const on = displayDate(entry.disclosed_on);
  const paper = entry.capture_method === 'manual_on_behalf';
  const interests = annual.interests ?? [];
  return (
    <div
      className="flex flex-col gap-8 border-l-2 border-border-subtle pl-12"
      data-testid={`coi-register-annual-${entry.record_id}`}
    >
      <span className={QUIET}>
        {paper ? w.annualFromPaper(annual.cycle_label, on) : w.annualSigned(annual.cycle_label, on)}
        {/* The words shown are the correction's (contract §10.1). */}
        {entry.superseded_by_record_id ? (
          <span data-testid={`coi-register-corrected-${entry.record_id}`}>
            {` · ${w.corrected}`}
          </span>
        ) : null}
      </span>
      {interests.length === 0 ? (
        <span className="font-sans text-s14 font-w400 tracking-givmo text-text-strong">
          {w.namesNoInterest}
        </span>
      ) : (
        <ul className="flex flex-col gap-6">
          {interests.map((interest) => {
            const description = interestDescription(interest);
            return (
              <li
                key={interest.entry_key}
                className="flex flex-wrap items-center gap-x-8 gap-y-2"
                data-testid={`coi-register-interest-${interest.entry_key}`}
              >
                <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                  {interest.name ?? interest.person ?? ''}
                </span>
                {description.length > 0 || interest.new_since ? (
                  <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                    {description}
                    {/* Plain secondary words, never a pill (the orchestrator's mark). */}
                    {interest.new_since ? (
                      <>
                        {description.length > 0 ? ' · ' : ''}
                        <span
                          className="whitespace-nowrap"
                          data-testid={`coi-register-new-${interest.entry_key}`}
                        >
                          {w.newSince(interest.new_since)}
                        </span>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {annual.replaces ? (
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {w.replaces(annual.replaces.cycle_label ?? '', displayDate(annual.replaces.occurred_on))}
        </span>
      ) : null}
      {entry.status === 'superseded' ? (
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {w.replacedByLaterForm}
        </span>
      ) : null}
      <Citations entry={entry} />
    </div>
  );
}

/**
 * Where an entry comes from, as links (y4): its record; the correction an annual entry's words
 * come from; and the meeting a single disclosure was made at. A correction's pointer is set only by
 * a correction, never by a replacement (the backend's `superseded_by_record_id`). For a year's
 * answers that pointer is the FIRST correction's seal, while the words shown are the latest
 * correction's: the annual block names that record itself (`in_force_record_id`, contract §10.1;
 * review-134's F2), and it is the one cited. A single disclosure shows its own words, so its
 * correction is the record that corrects them.
 */
function Citations({ entry }: { entry: ConflictDisclosureRow }) {
  const links: { recordId: string; label: string; testId: string }[] = [
    {
      recordId: entry.record_id,
      label: w.inMinuteBook,
      testId: `coi-cite-record-${entry.record_id}`,
    },
  ];
  const inForce = entry.annual?.in_force_record_id ?? null;
  const correction = entry.annual
    ? inForce !== null && inForce !== entry.record_id
      ? inForce
      : null
    : (entry.superseded_by_record_id ?? null);
  if (correction !== null)
    links.push({
      recordId: correction,
      label: w.theCorrection,
      testId: `coi-cite-correction-${entry.record_id}`,
    });
  if (entry.annual === null && entry.meeting_record_id)
    links.push({
      recordId: entry.meeting_record_id,
      label: w.meetingItWasMadeAt,
      testId: `coi-cite-meeting-${entry.record_id}`,
    });
  return (
    <span className="flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
      {links.map((link, index) => (
        <span key={link.testId} className="inline-flex items-center gap-6">
          {index > 0 ? <span aria-hidden>·</span> : null}
          <Link
            to={BOARD_PAGES.minuteBook + '/$recordId'}
            params={{ recordId: link.recordId }}
            className={CITE}
            data-testid={link.testId}
          >
            {link.label}
          </Link>
        </span>
      ))}
    </span>
  );
}

/** A disclosure made on its own (v0's): when, whom it names, and the person's own words (o8-register). */
function SingleBlock({ entry }: { entry: ConflictDisclosureRow }) {
  return (
    <div
      className="flex flex-col gap-4 border-l-2 border-border-subtle pl-12"
      data-testid={`coi-register-single-${entry.record_id}`}
    >
      <span className={QUIET}>{w.disclosedOnItsOwn(displayDate(entry.disclosed_on))}</span>
      {entry.counterparty_name ? (
        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {entry.counterparty_name}
        </span>
      ) : null}
      {/* The person's own words, whitespace kept: reflowing a disclosure changes the record read. */}
      <span className="whitespace-pre-wrap font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {entry.interest_summary}
      </span>
      {entry.status === 'superseded' ? (
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {w.replacedByLater}
        </span>
      ) : null}
      <Citations entry={entry} />
    </div>
  );
}
